import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Input, Button, Dropdown, Tooltip, message as antdMessage, Modal, Tabs, Table, Empty } from 'antd';
import { motion, AnimatePresence } from 'framer-motion';
import ReactECharts from 'echarts-for-react';
import {
  FolderOutlined,
  ShoppingCartOutlined,
  InboxOutlined,
  ToolOutlined,
  PlusOutlined,
  FileImageOutlined,
  SearchOutlined,
  LineChartOutlined,
  ApiOutlined,
  GoogleOutlined,
  CloudOutlined,
  ShareAltOutlined,
  RightOutlined,
  LikeOutlined,
  DislikeOutlined,
  EditOutlined,
  QuestionOutlined,
  ArrowDownOutlined,
  CheckOutlined,
  CloseOutlined,
  CopyOutlined,
  DownloadOutlined,
  UndoOutlined,
  SyncOutlined,
  ThunderboltOutlined,
  BulbOutlined,
  DatabaseOutlined,
  FireOutlined,
  RocketOutlined,
  LoadingOutlined
} from '@ant-design/icons';
import { enqueueFeedback } from '../utils/feedback';
import { downloadWordDoc, printAsPdf } from '../utils/exporters';
import { ReactComponent as SendIcon } from '../assets/svgs/sendIcon.svg';
import { sendQuery, getChatMessages } from '../services/api/chatService';
import MarkdownRenderer from '../components/common/MarkdownRenderer';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { useChatManager } from '../hooks/useChatManager';
import { useAuth } from '../hooks/useAuth';
import { Chat, ChatMessage as APIChatMessage } from '../types';

const { TextArea } = Input;

// Custom hook for typewriter effect
const useTypewriter = (text: string, speed: number = 50, stop: boolean = false) => {
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  
  useEffect(() => {
    if (text) {
      setDisplayedText('');
      setIsComplete(false);
      let i = 0;
      
      const timer = setInterval(() => {
        if (i < text.length) {
          setDisplayedText(text.slice(0, i + 1));
          i++;
        } else {
          setIsComplete(true);
          clearInterval(timer);
        }
      }, speed);
      
      return () => clearInterval(timer);
    }
  }, [text, speed]);

  // Stop streaming early when requested
  useEffect(() => {
    if (stop && !isComplete) {
      setIsComplete(true);
    }
  }, [stop, isComplete]);
  
  return { displayedText, isComplete };
};

// Typewriter component for AI responses with markdown support
const TypewriterText: React.FC<{
  text: string;
  messageId: string;
  onComplete?: (messageId: string) => void;
  stopStreaming?: boolean;
}> = ({ text, messageId, onComplete, stopStreaming }) => {
  const { displayedText, isComplete } = useTypewriter(text, 15, !!stopStreaming);

  useEffect(() => {
    if (isComplete && onComplete) {
      onComplete(messageId);
    }
  }, [isComplete, onComplete, messageId]);

  return (
    <div style={{ position: 'relative' }}>
      <MarkdownRenderer content={displayedText} />
      {!isComplete && (
        <span style={{
          marginLeft: '2px',
          animation: 'blink 1s infinite',
          color: '#FFFFFF'
        }}>
          |
        </span>
      )}
    </div>
  );
};

// Enhanced ECharts color palette
const DEFAULT_CHART_COLORS = [
  '#4ECDC4', // Teal
  '#FF6B6B', // Red
  '#95E1D3', // Light Teal
  '#FFA07A', // Light Salmon
  '#45B7D1', // Blue
  '#F38181', // Pink
  '#AA96DA', // Purple
  '#FCBAD3', // Light Pink
  '#FFFFD2', // Light Yellow
  '#A8D8EA', // Light Blue
  '#96CEB4', // Green
  '#FFEAA7', // Yellow
  '#DFE6E9', // Gray
  '#74B9FF', // Sky Blue
  '#FD79A8'  // Rose
];

/**
 * Thinking Animation Component with Sequential Rolling Text
 */
const ThinkingAnimation: React.FC<{ onStop: () => void }> = ({ onStop }) => {
  const thinkingStates = [
    { text: 'Thinking it through', icon: <BulbOutlined style={{ fontSize: '18px' }} />, duration: 0 },
    { text: 'Warming up the system', icon: <FireOutlined style={{ fontSize: '18px' }} />, duration: 2000 },
    { text: 'Thinking it through', icon: <SyncOutlined spin style={{ fontSize: '18px' }} />, duration: 4000 },
    { text: 'Processing the details', icon: <DatabaseOutlined style={{ fontSize: '18px' }} />, duration: 6000 },
    { text: 'Getting things ready for you', icon: <RocketOutlined style={{ fontSize: '18px' }} />, duration: 8000 }
  ];

  const [currentStateIndex, setCurrentStateIndex] = useState(0);
  const [dots, setDots] = useState('');

  useEffect(() => {
    // Animate dots every 500ms
    const dotsInterval = setInterval(() => {
      setDots((prev) => {
        if (prev.length >= 3) return '';
        return prev + '.';
      });
    }, 500);

    // Sequential state progression
    const timers: NodeJS.Timeout[] = [];

    thinkingStates.forEach((state, index) => {
      if (index > 0) {
        const timer = setTimeout(() => {
          setCurrentStateIndex(index);
        }, state.duration);
        timers.push(timer);
      }
    });

    return () => {
      clearInterval(dotsInterval);
      timers.forEach(timer => clearTimeout(timer));
    };
  }, []);

  const currentState = thinkingStates[currentStateIndex];

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0 }}
      style={{ width: '100%', textAlign: 'left', marginBottom: '8px' }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        color: '#B8B8B8',
        fontSize: '15px'
      }}>
        <AnimatePresence mode="wait">
          <motion.div
            key={currentStateIndex}
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 10 }}
            transition={{ duration: 0.3 }}
            style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
          >
            <span style={{ color: '#4ECDC4' }}>{currentState.icon}</span>
            <span style={{ fontStyle: 'italic', color: '#C5C5C5' }}>
              {currentState.text}
              <span style={{ display: 'inline-block', width: '30px', textAlign: 'left' }}>
                {dots}
              </span>
            </span>
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

/**
 * Enhance ECharts options with custom colors and dark theme settings
 */
const enhanceChartOptions = (options: any): any => {
  if (!options) {
    console.warn('enhanceChartOptions: Received null/undefined options, returning empty config');
    return {};
  }

  // Create a shallow copy and manually clone non-function properties
  // to avoid losing functions like valueFormatter during JSON.stringify
  const enhanced: any = { ...options };

  // Manually clone nested objects while preserving functions
  if (options.xAxis) {
    enhanced.xAxis = Array.isArray(options.xAxis)
      ? options.xAxis.map((axis: any) => ({ ...axis }))
      : { ...options.xAxis };
  }

  if (options.yAxis) {
    enhanced.yAxis = Array.isArray(options.yAxis)
      ? options.yAxis.map((axis: any) => ({ ...axis }))
      : { ...options.yAxis };
  }

  if (options.series) {
    enhanced.series = options.series.map((s: any) => ({ ...s }));
  }

  if (options.tooltip) {
    enhanced.tooltip = { ...options.tooltip };
  }

  if (options.legend) {
    enhanced.legend = { ...options.legend };
  }

  if (options.title) {
    enhanced.title = { ...options.title };
  }

  // ALWAYS override with our custom colors (ignore API colors)
  enhanced.color = DEFAULT_CHART_COLORS;

  // Hide title from chart (we display it above the chart)
  if (enhanced.title) {
    enhanced.title.show = false;
  }

  // Style axes for dark theme
  if (enhanced.xAxis) {
    const xAxis = Array.isArray(enhanced.xAxis) ? enhanced.xAxis : [enhanced.xAxis];
    xAxis.forEach((axis: any) => {
      axis.axisLine = { lineStyle: { color: 'rgba(255, 255, 255, 0.2)' }, ...axis.axisLine };
      axis.axisLabel = { color: '#C5C5C5', fontSize: 12, ...axis.axisLabel };
      axis.splitLine = { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' }, ...axis.splitLine };
      if (axis.name) {
        axis.nameTextStyle = { color: '#ECECEC', fontSize: 13, ...axis.nameTextStyle };
      }
    });
  }

  if (enhanced.yAxis) {
    const yAxis = Array.isArray(enhanced.yAxis) ? enhanced.yAxis : [enhanced.yAxis];
    yAxis.forEach((axis: any) => {
      axis.axisLine = { lineStyle: { color: 'rgba(255, 255, 255, 0.2)' }, ...axis.axisLine };
      axis.axisLabel = { color: '#C5C5C5', fontSize: 12, ...axis.axisLabel };
      axis.splitLine = { lineStyle: { color: 'rgba(255, 255, 255, 0.05)' }, ...axis.splitLine };
      if (axis.name) {
        axis.nameTextStyle = { color: '#ECECEC', fontSize: 13, ...axis.nameTextStyle };
      }
    });
  }

  // Style legend for dark theme
  if (enhanced.legend) {
    enhanced.legend.textStyle = {
      color: '#ECECEC',
      fontSize: 13,
      ...enhanced.legend.textStyle
    };
  }

  // Style tooltip (preserve any existing formatters)
  if (enhanced.tooltip) {
    enhanced.tooltip.backgroundColor = 'rgba(0, 0, 0, 0.85)';
    enhanced.tooltip.borderColor = 'rgba(255, 255, 255, 0.2)';
    enhanced.tooltip.textStyle = {
      color: '#ECECEC',
      fontSize: 13,
      ...enhanced.tooltip.textStyle
    };
    // Remove valueFormatter completely to avoid errors
    delete enhanced.tooltip.valueFormatter;
  }

  // FORCEFULLY apply colors using ECharts' color mechanism
  if (enhanced.series && Array.isArray(enhanced.series)) {
    enhanced.series.forEach((s: any, seriesIndex: number) => {
      // Use color callback function for dynamic coloring
      const colorIndex = seriesIndex % enhanced.color.length;

      // Set itemStyle with color callback for bars/pies
      if (s.type === 'bar' || s.type === 'pie') {
        s.itemStyle = {
          ...s.itemStyle,
          color: (params: any) => {
            // Use data index to get unique color for each bar/slice
            const idx = params.dataIndex % enhanced.color.length;
            return enhanced.color[idx];
          }
        };
      } else {
        // For line/scatter, use series color
        s.itemStyle = {
          ...s.itemStyle,
          color: enhanced.color[colorIndex]
        };
        s.lineStyle = {
          ...s.lineStyle,
          color: enhanced.color[colorIndex]
        };
      }

      // Ensure emphasis style exists for hover effects
      s.emphasis = {
        ...s.emphasis,
        itemStyle: {
          shadowBlur: 10,
          shadowColor: 'rgba(0, 0, 0, 0.5)',
          ...s.emphasis?.itemStyle
        }
      };
    });
  }

  return enhanced;
};

const NewChat: React.FC = () => {
  const { sessionId: urlSessionId } = useParams<{ sessionId?: string }>();
  const [message, setMessage] = useState('');
  const [showPlusDropdown, setShowPlusDropdown] = useState(false);
  const [showConnectorsSubmenu, setShowConnectorsSubmenu] = useState(false);
  const [showQuestionsPopup, setShowQuestionsPopup] = useState(false);
  const [showBottomPanel, setShowBottomPanel] = useState(false);
  const [selectedCard, setSelectedCard] = useState<string>('');
  const [chatMessages, setChatMessages] = useState<Array<{id: string, type: 'user' | 'assistant', content: string, timestamp: Date, table?: any, visualization?: any, followupSuggestions?: string[], sqlPreview?: string}>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [streamingId, setStreamingId] = useState<string | null>(null);
  const [stopRequestedFor, setStopRequestedFor] = useState<string | null>(null);
  const [chatTitle, setChatTitle] = useState('');
  const [userFeedback, setUserFeedback] = useState<{[key: string]: 'like' | 'dislike' | null}>({});
  // Simple feedback flow: immediate capture on click
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [unseenCount, setUnseenCount] = useState(0);
  const lastSeenRef = useRef<number>(0);
  const [completedResponses, setCompletedResponses] = useState<Set<string>>(new Set());

  // Details modal state
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedMessageData, setSelectedMessageData] = useState<any>(null);
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [hoveredFollowUp, setHoveredFollowUp] = useState<string | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<any>(null);
  const composerRef = useRef<any>(null);

  // Network status monitoring
  const { isOnline, wasOffline } = useNetworkStatus();

  // User context for API calls
  const { user } = useAuth();

  // Chat management
  const { currentChatId, setCurrentChatId, addNewChat, updateChatTitle, clearCurrentChat, chats } = useChatManager();

  // Rolling text state
  const rollingTexts = [
    "Your Command Center for Smart Decisions.",
    "Control Every Project. Effortlessly.",
    "One Platform. Total Control.",
    "The Nerve Center of Modern Construction.",
    "Orchestrate Your Enterprise from Here."
  ];
  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  // Change text every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTextIndex((prev) => (prev + 1) % rollingTexts.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [rollingTexts.length]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowPlusDropdown(false);
        setShowConnectorsSubmenu(false);
      }
      
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle scroll detection for scroll-to-bottom button
  useEffect(() => {
    const handleScroll = () => {
      if (chatContainerRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = chatContainerRef.current;
        const atBottom = scrollTop + clientHeight >= scrollHeight - 100;
        setIsAtBottom(atBottom);
        setShowScrollToBottom(!atBottom);
        if (atBottom) {
          setUnseenCount(0);
          lastSeenRef.current = chatMessages.length;
        }
      }
    };

    const chatContainer = chatContainerRef.current;
    if (chatContainer) {
      chatContainer.addEventListener('scroll', handleScroll);
      return () => chatContainer.removeEventListener('scroll', handleScroll);
    }
  }, [chatMessages]);

  // Track unseen messages when new ones arrive and user isn't at bottom
  useEffect(() => {
    if (isAtBottom) {
      lastSeenRef.current = chatMessages.length;
      setUnseenCount(0);
    } else if (chatMessages.length > lastSeenRef.current) {
      setUnseenCount(chatMessages.length - lastSeenRef.current);
    }
  }, [chatMessages.length, isAtBottom]);

  // Scroll to bottom function
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Global keyboard shortcuts
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Esc cancels pending/streaming
      if (e.key === 'Escape') {
        if (isLoading) {
          if (pendingTimerRef.current) {
            clearTimeout(pendingTimerRef.current);
            pendingTimerRef.current = null;
          }
          setIsLoading(false);
        } else if (streamingId) {
          setStopRequestedFor(streamingId);
          setCompletedResponses(prev => new Set(Array.from(prev).concat(streamingId)));
          setStreamingId(null);
        }
      }
      // Cmd/Ctrl + J focuses composer
      if ((e.ctrlKey || e.metaKey) && (e.key.toLowerCase() === 'j')) {
        e.preventDefault();
        composerRef.current?.focus?.();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isLoading, streamingId]);

  // Load chat messages when urlSessionId changes
  useEffect(() => {
    const loadChatMessages = async () => {
      // If no sessionId in URL, clear messages for new chat
      if (!urlSessionId) {
        console.log('🆕 No session ID in URL - showing empty chat');
        setChatMessages([]);
        setChatTitle('');
        setCompletedResponses(new Set());
        return;
      }

      console.log('📥 Loading messages from /chats/messages API for session:', urlSessionId);

      try {
        // ===== USING /chats/messages API =====
        const response = await getChatMessages(urlSessionId, 100);

        // Convert API messages to internal format
        const convertedMessages = response.messages.map((apiMsg: any) => {
          let content = apiMsg.content;
          let table = undefined;
          let visualization = undefined;
          let followupSuggestions: string[] = [];

          // If content is an object (assistant response), extract structured data
          if (typeof apiMsg.content === 'object' && apiMsg.content !== null) {
            content = apiMsg.content.answer || JSON.stringify(apiMsg.content);
            // Safely handle null artifacts - chart config is at artifacts.visualization.echarts_option
            table = apiMsg.content.artifacts?.table?.table || undefined;
            visualization = apiMsg.content.artifacts?.visualization?.echarts_option || undefined;
            followupSuggestions = apiMsg.content.follow_ups || [];
          }

          return {
            id: String(apiMsg.message_id),
            type: apiMsg.role as 'user' | 'assistant',
            content: typeof content === 'string' ? content : JSON.stringify(content),
            timestamp: new Date(apiMsg.created_at),
            table: table || undefined, // Ignore if null
            visualization: visualization || undefined, // Ignore if null
            followupSuggestions: followupSuggestions.length > 0 ? followupSuggestions : undefined
          };
        });

        // Set the messages
        setChatMessages(convertedMessages);

        // Mark all loaded messages as completed (enables toolbar)
        const messageIds = convertedMessages.map(msg => msg.id);
        setCompletedResponses(new Set(messageIds));

        // Generate chat title from first user message
        const firstUserMessage = convertedMessages.find(msg => msg.type === 'user');
        if (firstUserMessage) {
          setChatTitle(generateChatTitle(firstUserMessage.content));
          console.log('📝 Generated chat title from first message');
        }

        // Scroll to bottom after loading
        setTimeout(scrollToBottom, 100);

        console.log('✅ Loaded', convertedMessages.length, 'messages from API');
      } catch (error) {
        console.error('❌ Failed to load chat messages:', error);
        // Silently fail - don't show error to user
      }
    };

    loadChatMessages();
  }, [urlSessionId, chats]);

  // Generate follow-up questions based on AI response
  // Commented out default questions - using AI-generated suggestions from backend instead
  const generateFollowUpQuestions = (response: string, category?: string): string[] => {
    // Return empty array - follow-up questions now come from API response
    return [];

    /* Default questions removed - using backend suggestions
    const baseQuestions = [
      "Can you provide more details about this?",
      "What are the next steps?",
      "How does this compare to industry standards?",
      "What potential issues should I watch for?"
    ];

    if (response.toLowerCase().includes('project')) {
      return [
        "What's the timeline for these projects?",
        "How can we accelerate delayed projects?",
        "What resources are needed?",
        "Show me project budget breakdown"
      ];
    } else if (response.toLowerCase().includes('procurement')) {
      return [
        "Which vendors should we prioritize?",
        "How can we reduce procurement costs?",
        "What's our backup supplier strategy?",
        "Show me vendor performance metrics"
      ];
    } else if (response.toLowerCase().includes('inventory')) {
      return [
        "What's our optimal reorder point?",
        "How can we reduce carrying costs?",
        "Which items are overstocked?",
        "Show me inventory turnover analysis"
      ];
    } else if (response.toLowerCase().includes('work order')) {
      return [
        "How can we improve completion rates?",
        "What training do teams need?",
        "Which work orders need priority?",
        "Show me maintenance schedule"
      ];
    }

    return baseQuestions;
    */
  };

  // Handle title editing
  const handleTitleEdit = () => {
    setIsEditingTitle(true);
    setTimeout(() => titleRef.current?.focus(), 0);
  };

  const handleTitleSave = () => {
    setIsEditingTitle(false);
  };

  const handleTitleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleTitleSave();
    } else if (e.key === 'Escape') {
      setIsEditingTitle(false);
    }
  };

  const dropdownMenuItems = [
    {
      icon: <FileImageOutlined />,
      title: 'Add photos & files',
      action: () => console.log('Add photos & files')
    },
    {
      icon: <SearchOutlined />,
      title: 'Deep research',
      action: () => console.log('Deep research')
    },
    {
      icon: <LineChartOutlined />,
      title: 'Forecasting & Predictions',
      action: () => console.log('Forecasting & Predictions')
    },
    {
      icon: <ApiOutlined />,
      title: 'Connectors',
      hasSubmenu: true,
      action: () => console.log('Connectors clicked'),
      submenu: [
        { icon: <GoogleOutlined />, title: 'Connect Google Drive', action: () => console.log('Google Drive') },
        { icon: <CloudOutlined />, title: 'Connect OneDrive', action: () => console.log('OneDrive') },
        { icon: <ShareAltOutlined />, title: 'Connect SharePoint', action: () => console.log('SharePoint') }
      ]
    }
  ];

  const cardQuestions = {
    'Projects': [
      'How is the overall project progress?',
      'What are the current project milestones and deadlines?',
      'Which projects are behind schedule and why?',
      'What are the budget vs actual costs for active projects?',
      'Show me resource allocation across all projects'
    ],
    'Procurement': [
      'How is procurement going overall?',
      'What are the pending purchase orders?',
      'Which vendors have the best performance ratings?',
      'What materials are currently on backorder?',
      'Show me procurement budget vs actual spending'
    ],
    'Inventory': [
      'What is our current inventory status?',
      'Which materials are running low and need reordering?',
      'Show me inventory turnover rates by category',
      'What is the total value of current inventory?',
      'Which items have been in stock the longest?'
    ],
    'Work Orders': [
      'How many work orders are currently open?',
      'What are the highest priority work orders?',
      'Which work orders are overdue?',
      'Show me work order completion rates by team',
      'What are the common issues in recent work orders?'
    ],
    'Others': [
      'Can you provide more details about this?',
      'What are the next steps?',
      'How does this compare to industry standards?',
      'What potential issues should I watch for?'
    ]
  };

  const suggestionCards = [
    {
      icon: <ShoppingCartOutlined />,
      title: 'Procurement',
      description: 'Handle material procurement'
    },
    {
      icon: <InboxOutlined />,
      title: 'Inventory',
      description: 'Track inventory and materials'
    },
    {
      icon: <FolderOutlined />,
      title: 'Projects',
      description: 'Manage construction projects'
    },
    {
      icon: <ToolOutlined />,
      title: 'Work Orders',
      description: 'Create and manage work orders'
    },
    {
      icon: <QuestionOutlined />,
      title: 'Others',
      description: 'General help and queries'
    }
  ];

  const handleSend = async () => {
    if (message.trim()) {
      const userMessage = {
        id: Date.now().toString(),
        type: 'user' as const,
        content: message.trim(),
        timestamp: new Date()
      };

      // Set chat title if this is the first message
      if (chatMessages.length === 0 && !chatTitle) {
        setChatTitle(generateChatTitle(message.trim()));
      }

      setChatMessages(prev => [...prev, userMessage]);
      const questionToSend = message.trim();
      setMessage('');
      setIsLoading(true);

      try {
        // ===== USING /qa/answer API ONLY =====
        // Call QA API with user context (no session ID)
        if (!user?.id || !user?.orgid) {
          throw new Error('User context not available');
        }

        console.log('📡 Calling /qa/answer API:', {
          question: questionToSend.substring(0, 50) + '...',
          userId: user.id,
          orgId: user.orgid
        });

        const response = await sendQuery(
          questionToSend,
          user.id,
          user.orgid,
          null // No session ID passed
        );

        // Set chat title from first query if not set
        if (chatMessages.length === 0 && !chatTitle) {
          const generatedTitle = questionToSend.length > 50
            ? questionToSend.substring(0, 47) + '...'
            : questionToSend;
          setChatTitle(generatedTitle);
        }

        // Extract response text from answer field
        const answerText = response.answer || 'No response from AI.';

        // Safely extract artifacts - handle null/undefined
        // Chart config is at: artifacts.visualization.echarts_option
        const aiResponse = {
          id: (Date.now() + 1).toString(),
          type: 'assistant' as const,
          content: answerText,
          timestamp: new Date(),
          table: response.artifacts?.table?.table || undefined, // Nested path: artifacts.table.table
          visualization: response.artifacts?.visualization?.echarts_option || undefined, // Nested path: artifacts.visualization.echarts_option
          followupSuggestions: response.follow_ups || undefined
        };

        setChatMessages(prev => [...prev, aiResponse]);
        setStreamingId(aiResponse.id);
        setStopRequestedFor(null);

      } catch (error) {
        console.error('❌ Chat error:', error);

        // Show error message to user
        const errorResponse = {
          id: (Date.now() + 1).toString(),
          type: 'assistant' as const,
          content: 'Sorry, I encountered an error processing your request. Please try again.',
          timestamp: new Date()
        };

        setChatMessages(prev => [...prev, errorResponse]);
        setStreamingId(errorResponse.id);

        antdMessage.error('Failed to get response. Please try again.');
      } finally {
        setIsLoading(false);
        pendingTimerRef.current = null;
      }
    }
  };

  // Generate smart chat title from first user message
  const generateChatTitle = (message: string, category?: string): string => {
    if (category) {
      return `${category} Discussion`;
    }
    
    // Extract meaningful title from user message
    const cleanMessage = message.trim();
    if (cleanMessage.length <= 50) {
      return cleanMessage;
    }
    
    // Truncate intelligently at word boundary
    const truncated = cleanMessage.substring(0, 47);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    return lastSpaceIndex > 20 ? truncated.substring(0, lastSpaceIndex) + '...' : truncated + '...';
  };

  // Handle user feedback
  const handleFeedback = (messageId: string, sentiment: 'like' | 'dislike') => {
    setUserFeedback(prev => ({ ...prev, [messageId]: sentiment }));
    const idx = chatMessages.findIndex((m) => m.id === messageId);
    const prevUserMessage = idx > 0 && chatMessages[idx - 1].type === 'user' ? chatMessages[idx - 1].content : undefined;
    enqueueFeedback({
      id: `${messageId}-${Date.now()}`,
      messageId,
      sentiment,
      reasons: [],
      timestamp: new Date().toISOString(),
      comment: undefined,
      snippet: undefined,
      context: { prevUserMessage }
    });
    antdMessage.success(sentiment === 'like' ? 'Thanks for the feedback!' : 'Thanks — we will use this to improve.');
  };

  // Retry last AI response
  const handleRetry = async (messageId: string) => {
    const messageIndex = chatMessages.findIndex(msg => msg.id === messageId);
    if (messageIndex > 0) {
      const previousUserMessage = chatMessages[messageIndex - 1];
      if (previousUserMessage.type === 'user') {
        setIsLoading(true);

        // Remove the current AI response and regenerate
        const updatedMessages = chatMessages.slice(0, messageIndex);
        setChatMessages(updatedMessages);

        try {
          // ===== USING /qa/answer API ONLY =====
          // Use QA API for retry (no session ID)
          if (!user?.id || !user?.orgid) {
            throw new Error('User context not available');
          }

          console.log('🔄 Retrying with /qa/answer API:', {
            question: previousUserMessage.content.substring(0, 50) + '...'
          });

          const response = await sendQuery(
            previousUserMessage.content,
            user.id,
            user.orgid,
            null // No session ID passed
          );

          const answerText = response.answer || 'No response from AI.';

          // Safely extract artifacts - handle null/undefined
          // Chart config is at: artifacts.visualization.echarts_option
          const newAIResponse = {
            id: Date.now().toString(),
            type: 'assistant' as const,
            content: answerText,
            timestamp: new Date(),
            table: response.artifacts?.table?.table || undefined, // Nested path: artifacts.table.table
            visualization: response.artifacts?.visualization?.echarts_option || undefined, // Nested path: artifacts.visualization.echarts_option
            followupSuggestions: response.follow_ups || undefined
          };

          setChatMessages(prev => [...prev, newAIResponse]);
          setStreamingId(newAIResponse.id);
          setStopRequestedFor(null);

        } catch (error) {
          console.error('❌ Retry error:', error);

          const errorResponse = {
            id: Date.now().toString(),
            type: 'assistant' as const,
            content: 'Sorry, I encountered an error retrying your request. Please try again.',
            timestamp: new Date()
          };

          setChatMessages(prev => [...prev, errorResponse]);
          setStreamingId(errorResponse.id);

          antdMessage.error('Failed to retry. Please try again.');
        } finally {
          setIsLoading(false);
          pendingTimerRef.current = null;
        }
      }
    }
  };


  // Handle typewriter completion
  const handleTypewriterComplete = useCallback((messageId: string) => {
    setCompletedResponses(prev => new Set(Array.from(prev).concat(messageId)));
    if (streamingId === messageId) {
      setStreamingId(null);
    }
  }, [streamingId]);

  // Handle follow-up question click
  const handleFollowUpClick = (question: string) => {
    // Directly send the question without relying on state update
    handleSendWithQuestion(question);
  };

  // Helper function to send a specific question (for follow-ups)
  const handleSendWithQuestion = async (questionText: string) => {
    if (questionText.trim()) {
      const userMessage = {
        id: Date.now().toString(),
        type: 'user' as const,
        content: questionText.trim(),
        timestamp: new Date()
      };

      // Set chat title if this is the first message
      if (chatMessages.length === 0 && !chatTitle) {
        setChatTitle(generateChatTitle(questionText.trim()));
      }

      setChatMessages(prev => [...prev, userMessage]);
      setMessage(''); // Clear input field
      setIsLoading(true);

      try {
        // ===== USING /qa/answer API ONLY =====
        // Call QA API with user context (no session ID)
        if (!user?.id || !user?.orgid) {
          throw new Error('User context not available');
        }

        console.log('📡 Calling /qa/answer API (follow-up):', {
          question: questionText.substring(0, 50) + '...',
          userId: user.id,
          orgId: user.orgid
        });

        const response = await sendQuery(
          questionText.trim(),
          user.id,
          user.orgid,
          null // No session ID passed
        );

        // Set chat title from first query if not set
        if (chatMessages.length === 0 && !chatTitle) {
          const generatedTitle = questionText.length > 50
            ? questionText.substring(0, 47) + '...'
            : questionText;
          setChatTitle(generatedTitle);
        }

        // Extract response text from answer field
        const answerText = response.answer || 'No response from AI.';

        // Safely extract artifacts - handle null/undefined
        // Chart config is at: artifacts.visualization.echarts_option
        const aiResponse = {
          id: (Date.now() + 1).toString(),
          type: 'assistant' as const,
          content: answerText,
          timestamp: new Date(),
          table: response.artifacts?.table?.table || undefined, // Nested path: artifacts.table.table
          visualization: response.artifacts?.visualization?.echarts_option || undefined, // Nested path: artifacts.visualization.echarts_option
          followupSuggestions: response.follow_ups || undefined
        };

        setChatMessages(prev => [...prev, aiResponse]);
        setStreamingId(aiResponse.id);
        setStopRequestedFor(null);

      } catch (error) {
        console.error('❌ Chat error:', error);

        // Show error message to user
        const errorResponse = {
          id: (Date.now() + 1).toString(),
          type: 'assistant' as const,
          content: 'Sorry, I encountered an error processing your request. Please try again.',
          timestamp: new Date()
        };

        setChatMessages(prev => [...prev, errorResponse]);
        setStreamingId(errorResponse.id);

        antdMessage.error('Failed to get response. Please try again.');
      } finally {
        setIsLoading(false);
        pendingTimerRef.current = null;
      }
    }
  };

  const handleQuestionSelect = async (question: string) => {
    setShowQuestionsPopup(false);

    // Set chat title based on selected category
    setChatTitle(generateChatTitle(question, selectedCard));

    // Create user message directly
    const userMessage = {
      id: Date.now().toString(),
      type: 'user' as const,
      content: question,
      timestamp: new Date()
    };

    setChatMessages([userMessage]);
    setIsLoading(true);

    try {
      // Call new QA API (first message, no session_id)
      if (!user?.id || !user?.orgid) {
        throw new Error('User context not available');
      }

      const response = await sendQuery(
        question,
        user.id,
        user.orgid,
        null // No session ID
      );

      // Extract response text
      const answerText = response.answer || 'No response from AI.';

      // Safely extract artifacts - handle null/undefined
      // Chart config is at: artifacts.visualization.echarts_option
      const aiResponse = {
        id: (Date.now() + 1).toString(),
        type: 'assistant' as const,
        content: answerText,
        timestamp: new Date(),
        table: response.artifacts?.table?.table || undefined, // Nested path: artifacts.table.table
        visualization: response.artifacts?.visualization?.echarts_option || undefined, // Nested path: artifacts.visualization.echarts_option
        followupSuggestions: response.follow_ups || undefined
      };

      setChatMessages(prev => [...prev, aiResponse]);
      setStreamingId(aiResponse.id);
      setStopRequestedFor(null);

    } catch (error) {
      console.error('❌ Chat API error:', error);

      const errorResponse = {
        id: (Date.now() + 1).toString(),
        type: 'assistant' as const,
        content: 'Sorry, I encountered an error processing your request. Please try again.',
        timestamp: new Date()
      };

      setChatMessages(prev => [...prev, errorResponse]);
      setStreamingId(errorResponse.id);

      antdMessage.error('Failed to get AI response. Please try again.');
    } finally {
      setIsLoading(false);
      pendingTimerRef.current = null;
    }
  };

  // Main component render
  return (
    <>
      {/* Show chat interface if there are messages */}
      {chatMessages.length > 0 ? (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 48px)',
        width: '100%',
        maxWidth: '1100px',
        margin: '0 auto',
        padding: '0 20px',
        position: 'relative'
      }}>
        {/* Network Status Banner */}
        <AnimatePresence>
          {!isOnline && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              style={{
                position: 'fixed',
                top: '60px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1000,
                background: 'linear-gradient(135deg, #ff6b35 0%, #f44336 100%)',
                color: '#FFFFFF',
                padding: '12px 24px',
                borderRadius: '12px',
                boxShadow: '0 4px 16px rgba(244, 67, 54, 0.4)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div className="spinner" style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid rgba(255, 255, 255, 0.3)',
                  borderTop: '2px solid #FFFFFF',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite'
                }} />
                <span>Network connection lost. Attempting to reconnect…</span>
              </div>
            </motion.div>
          )}

          {wasOffline && isOnline && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              style={{
                position: 'fixed',
                top: '60px',
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: 1000,
                background: 'linear-gradient(135deg, #4CAF50 0%, #52c41a 100%)',
                color: '#FFFFFF',
                padding: '12px 24px',
                borderRadius: '12px',
                boxShadow: '0 4px 16px rgba(76, 175, 80, 0.4)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              <CheckOutlined style={{ fontSize: '16px' }} />
              <span>You're back online! Continue chatting</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Chat Title Header - Inline Editing */}
        <motion.div
          style={{
            padding: '20px 0 16px 0',
            borderBottom: '1px solid #2c2f2f',
            marginBottom: '16px',
            flexShrink: 0
          }}
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              flex: 1
            }}>
              {isEditingTitle ? (
                <motion.div
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: '12px',
                    width: '100%',
                    maxWidth: '600px'
                  }}
                  initial={{ scale: 0.95 }}
                  animate={{ scale: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <Input
                    ref={titleRef}
                    value={chatTitle || 'New Conversation'}
                    onChange={(e) => setChatTitle(e.target.value)}
                    onBlur={handleTitleSave}
                    onKeyDown={handleTitleKeyPress}
                    style={{
                      background: 'transparent',
                      border: '1px solid #2c2f2f',
                      borderRadius: '8px',
                      color: '#FFFFFF',
                      fontSize: '20px',
                      fontWeight: '600',
                      padding: '8px 16px',
                      flex: 1,
                      minWidth: '300px'
                    }}
                  />
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                    <Button
                      type="text"
                      icon={<CheckOutlined />}
                      size="small"
                      onClick={handleTitleSave}
                      style={{ 
                        color: '#4CAF50',
                        width: '32px',
                        height: '32px'
                      }}
                    />
                    <Button
                      type="text"
                      icon={<CloseOutlined />}
                      size="small"
                      onClick={() => setIsEditingTitle(false)}
                      style={{ 
                        color: '#8A8A8A',
                        width: '32px',
                        height: '32px'
                      }}
                    />
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    width: '100%',
                    maxWidth: '600px',
                    padding: '4px 8px',
                    borderRadius: '8px',
                    border: '1px solid transparent'
                  }}
                  whileHover={{ 
                    scale: 1.01,
                    borderColor: '#2c2f2f'
                  }}
                  onClick={handleTitleEdit}
                >
                  <h1 style={{
                    color: '#FFFFFF',
                    fontSize: '20px',
                    fontWeight: '600',
                    margin: 0
                  }}>
                    {chatTitle || 'New Conversation'}
                  </h1>
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileHover={{ opacity: 1 }}
                    transition={{ duration: 0.2 }}
                    style={{
                      padding: '4px',
                      borderRadius: '4px'
                    }}
                  >
                    <EditOutlined style={{ 
                      color: '#8A8A8A', 
                      fontSize: '16px',
                      cursor: 'pointer'
                    }} />
                  </motion.div>
                </motion.div>
              )}
            </div>
            <div style={{
              color: '#8A8A8A',
              fontSize: '12px'
            }}>
              {chatMessages.length} message{chatMessages.length !== 1 ? 's' : ''}
            </div>
          </div>
        </motion.div>

        {/* Chat Messages */}
        <div 
          ref={chatContainerRef}
          className="hide-scrollbar"
          role="log"
          aria-live="polite"
          aria-relevant="additions text"
          aria-label="Chat messages"
          style={{
            flex: 1,
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            paddingRight: '4px'
          }}
        >
          {chatMessages.map((msg) => (
            <motion.div
              key={msg.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              style={{ width: '100%' }}
              onMouseEnter={() => setHoveredMessageId(msg.id)}
              onMouseLeave={() => setHoveredMessageId(null)}
            >
              {/* Message Content - Full Width, No Background */}
              <div style={{
                width: '100%',
                textAlign: msg.type === 'user' ? 'right' : 'left',
                marginBottom: '12px'
              }}>
                <div style={{
                  display: 'inline-block',
                  width: '100%',
                  color: '#FFFFFF',
                  fontSize: '18px',
                  lineHeight: '1.7',
                  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif'
                }}>
                  {msg.type === 'assistant' ? (
                    // Only use typewriter for streaming messages, not loaded messages
                    streamingId === msg.id && !completedResponses.has(msg.id) ? (
                      <TypewriterText
                        text={msg.content}
                        messageId={msg.id}
                        onComplete={handleTypewriterComplete}
                        stopStreaming={stopRequestedFor === msg.id}
                      />
                    ) : (
                      <MarkdownRenderer content={msg.content} />
                    )
                  ) : (
                    <MarkdownRenderer content={msg.content} />
                  )}
                </div>

                {/* Inline Chart Visualization - Using artifacts.visualization.echarts_option */}
                {msg.type === 'assistant' &&
                 completedResponses.has(msg.id) &&
                 msg.visualization && (() => {
                  try {
                    return (
                      <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4, delay: 0.1 }}
                        style={{
                          marginTop: '24px',
                          background: 'rgba(255, 255, 255, 0.03)',
                          border: '1px solid rgba(255, 255, 255, 0.08)',
                          borderRadius: '12px',
                          padding: '20px',
                          overflow: 'hidden'
                        }}
                      >
                        {/* Chart Title from echarts_option */}
                        {msg.visualization?.title?.text && (
                          <div style={{
                            fontSize: '17px',
                            fontWeight: '600',
                            color: '#ECECEC',
                            marginBottom: '16px',
                            paddingBottom: '12px',
                            borderBottom: '1px solid rgba(255, 255, 255, 0.05)'
                          }}>
                            {msg.visualization.title.text}
                            {msg.visualization.title.subtext && (
                              <div style={{
                                fontSize: '13px',
                                fontWeight: '400',
                                color: '#C5C5C5',
                                marginTop: '4px'
                              }}>
                                {msg.visualization.title.subtext}
                              </div>
                            )}
                          </div>
                        )}
                        <ReactECharts
                          option={enhanceChartOptions(msg.visualization)}
                          style={{ height: '500px', width: '100%' }}
                          opts={{ renderer: 'svg' }}
                        />
                      </motion.div>
                    );
                  } catch (error) {
                    console.warn('Chart rendering error (ignoring):', error);
                    return null;
                  }
                })()}

                {/* View Full Screen Link - Show if table or visualization exists */}
                {msg.type === 'assistant' &&
                 completedResponses.has(msg.id) &&
                 (msg.table || msg.visualization) && (
                  <div style={{ marginTop: '12px' }}>
                    <Button
                      type="link"
                      onClick={() => {
                        setSelectedMessageData(msg);
                        setDetailsModalVisible(true);
                      }}
                      style={{
                        color: '#6B7280',
                        fontSize: '14px',
                        padding: 0,
                        height: 'auto',
                        fontWeight: '500'
                      }}
                    >
                      {msg.visualization && msg.table ? '🔍 View Full Screen & Data Table →' :
                       msg.visualization ? '🔍 View Full Screen Chart →' :
                       '📊 View Data Table →'}
                    </Button>
                  </div>
                )}
              </div>

              {/* Timestamp on hover */}
              <div style={{
                textAlign: msg.type === 'user' ? 'right' : 'left',
                color: '#8A8A8A',
                fontSize: '12px',
                height: '16px',
                opacity: hoveredMessageId === msg.id ? 1 : 0,
                transition: 'opacity 0.2s ease'
              }}>
                {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>

              {/* Enhanced Toolbar - Only for AI responses after completion */}
              {msg.type === 'assistant' && streamingId === msg.id && !completedResponses.has(msg.id) && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
                  <Button type="link" onClick={() => {
                    setStopRequestedFor(msg.id);
                    setCompletedResponses(prev => new Set(Array.from(prev).concat(msg.id)));
                    setStreamingId(null);
                  }}>Stop</Button>
                </div>
              )}

              {/* Enhanced Toolbar - Only for AI responses after completion */}
              {msg.type === 'assistant' && completedResponses.has(msg.id) && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: '8px',
                    marginBottom: '16px'
                  }}
                >
                  {copiedId === msg.id && (
                    <span style={{ color: '#8A8A8A', fontSize: '12px' }}>Copied!</span>
                  )}
                  {/* Download Dropdown */}
                  <Dropdown
                    menu={{
                      items: [
                        { key: 'doc', label: 'Word (.doc)' },
                        { key: 'pdf', label: 'PDF' }
                      ],
                      onClick: ({ key }) => {
                        const base = (chatTitle || 'chat')
                          .toLowerCase()
                          .replace(/[^a-z0-9-_]+/g, '-')
                          .replace(/^-+|-+$/g, '')
                          .slice(0, 40) || 'chat';
                        if (key === 'doc') {
                          downloadWordDoc(`${base}-${msg.id}.doc`, msg.content, chatTitle || 'Chat');
                        } else if (key === 'pdf') {
                          printAsPdf(msg.content, chatTitle || 'Chat');
                        }
                      }
                    }}
                    placement="bottomRight"
                    trigger={["click"]}
                  >
                    <Button
                      type="text"
                      icon={<DownloadOutlined />}
                      size="small"
                      style={{
                        color: '#8A8A8A',
                        border: 'none',
                        background: 'transparent',
                        padding: '4px 8px',
                        borderRadius: '6px'
                      }}
                    />
                  </Dropdown>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      type="text"
                      icon={<CopyOutlined />}
                      size="small"
                      style={{
                        color: '#8A8A8A',
                        border: 'none',
                        background: 'transparent',
                        padding: '4px 8px',
                        borderRadius: '6px'
                      }}
                      onClick={() => {
                        navigator.clipboard.writeText(msg.content);
                        setCopiedId(msg.id);
                        setTimeout(() => setCopiedId(null), 1200);
                      }}
                    />
                  </motion.div>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      type="text"
                      icon={<UndoOutlined />}
                      size="small"
                      style={{
                        color: '#8A8A8A',
                        border: 'none',
                        background: 'transparent',
                        padding: '4px 8px',
                        borderRadius: '6px'
                      }}
                      onClick={() => handleRetry(msg.id)}
                    />
                  </motion.div>
                  {userFeedback[msg.id] !== 'dislike' && (
                  <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                    <Button
                      type="text"
                      icon={<LikeOutlined />}
                      size="small"
                      style={{
                        color: userFeedback[msg.id] === 'like' ? '#4CAF50' : '#8A8A8A',
                        border: 'none',
                        background: 'transparent',
                        padding: '4px 8px',
                        borderRadius: '6px'
                      }}
                      onClick={() => handleFeedback(msg.id, 'like')}
                    />
                  </motion.div>
                  )}
                  {userFeedback[msg.id] !== 'like' && (
                    <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                      <Button
                        type="text"
                        icon={<DislikeOutlined />}
                        size="small"
                        style={{
                          color: userFeedback[msg.id] === 'dislike' ? '#F44336' : '#8A8A8A',
                          border: 'none',
                          background: 'transparent',
                          padding: '4px 8px',
                          borderRadius: '6px'
                        }}
                        onClick={() => handleFeedback(msg.id, 'dislike')}
                      />
                    </motion.div>
                  )}
                </motion.div>
              )}

              {/* Follow-up Questions - Only for AI responses after completion */}
              {msg.type === 'assistant' && completedResponses.has(msg.id) && (msg.followupSuggestions || generateFollowUpQuestions(msg.content).length > 0) && (
                <motion.div
                  initial="hidden"
                  animate="show"
                  variants={{
                    hidden: { opacity: 0, y: 6 },
                    show: {
                      opacity: 1,
                      y: 0,
                      transition: { delayChildren: 0.2, staggerChildren: 0.08 }
                    }
                  }}
                  style={{ marginBottom: '8px' }}
                >
                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    marginTop: '16px'
                  }}>
                    {(msg.followupSuggestions || generateFollowUpQuestions(msg.content).slice(0, 4)).map((question, index) => {
                      const key = `${msg.id}-${index}`;
                      const isHovered = hoveredFollowUp === key;
                      return (
                        <motion.button
                          key={key}
                          variants={{ hidden: { opacity: 0, y: 8 }, show: { opacity: 1, y: 0 } }}
                          whileHover={{ backgroundColor: '#2c2f2f' }}
                          whileTap={{ scale: 0.98 }}
                          animate={isHovered ? { scale: [1, 1.03, 1] } : { scale: 1 }}
                          transition={isHovered ? { duration: 0.8, repeat: Infinity } : {}}
                          onMouseEnter={() => setHoveredFollowUp(key)}
                          onMouseLeave={() => setHoveredFollowUp(null)}
                          onClick={() => handleFollowUpClick(question)}
                          style={{
                            background: 'transparent',
                            border: '1px solid #2c2f2f',
                            borderRadius: '20px',
                            padding: '8px 16px',
                            color: '#B8B8B8',
                            fontSize: '14px',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          {question}
                        </motion.button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </motion.div>
          ))}
          
          {/* Loading indicator with rolling text */}
          {isLoading && <ThinkingAnimation onStop={() => {
            if (pendingTimerRef.current) {
              clearTimeout(pendingTimerRef.current);
              pendingTimerRef.current = null;
            }
            setIsLoading(false);
          }} />}
          
          {/* Scroll anchor */}
          <div ref={messagesEndRef} />
        </div>

        {/* Scroll to Bottom Button */}
          {showScrollToBottom && (
          <Tooltip title="Jump to latest" placement="left">
          <motion.button
            initial={{ opacity: 0, scale: 0.8 }}
            animate={unseenCount > 0 ? { opacity: 1, scale: [1, 1.04, 1] } : { opacity: 1, scale: 1 }}
            transition={unseenCount > 0 ? { duration: 1.2, repeat: Infinity } : {}}
            exit={{ opacity: 0, scale: 0.8 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={scrollToBottom}
            style={{
              position: 'absolute',
              bottom: '80px',
              right: '20px',
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              background: '#2c2f2f',
              border: '1px solid #404040',
              color: '#B8B8B8',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10,
              boxShadow: unseenCount > 0
                ? '0 0 0 2px rgba(37,99,235,0.25), 0 4px 12px rgba(0,0,0,0.35)'
                : '0 2px 8px rgba(0, 0, 0, 0.3)'
            }}
            aria-label={unseenCount > 0 ? `Jump to latest, ${unseenCount} new messages` : 'Jump to latest'}
          >
            <ArrowDownOutlined style={{ fontSize: '16px' }} />
            {unseenCount > 0 && (
              <motion.span
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                style={{
                  position: 'absolute',
                  top: '-6px',
                  right: '-6px',
                  minWidth: '18px',
                  height: '18px',
                  padding: '0 4px',
                  borderRadius: '9px',
                  background: '#2563EB',
                  color: '#fff',
                  fontSize: '12px',
                  lineHeight: '18px',
                  textAlign: 'center',
                  boxShadow: '0 0 0 2px #191a1a'
                }}
                aria-label={`${unseenCount} new messages`}
              >
                {unseenCount}
              </motion.span>
            )}
          </motion.button>
          </Tooltip>
          )}

        {/* Input Area */}
        <div style={{
          background: '#1f2121',
          border: '1px solid #2c2f2f',
          borderRadius: '32px',
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px',
          minHeight: '56px',
          position: 'relative',
          flexShrink: 0,
          marginTop: '16px'
        }}>
          <div ref={dropdownRef} style={{ position: 'relative' }}>
            <Button
              type="text"
              icon={<PlusOutlined />}
              onClick={() => setShowPlusDropdown(!showPlusDropdown)}
              style={{
                color: '#8A8A8A',
                border: 'none',
                background: 'transparent',
                width: '24px',
                height: '24px',
                minWidth: '24px',
                padding: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            />
            
            {/* Plus Dropdown Menu */}
            {showPlusDropdown && (
              <motion.div
                initial={{ opacity: 0, y: -10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.95 }}
                transition={{ duration: 0.15 }}
                style={{
                  position: 'absolute',
                  bottom: '40px',
                  left: '-12px',
                  background: '#2c2f2f',
                  border: '1px solid #404040',
                  borderRadius: '12px',
                  padding: '8px',
                  minWidth: '240px',
                  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
                  zIndex: 1000
                }}
              >
                {dropdownMenuItems.map((item, index) => (
                  <div key={item.title} style={{ position: 'relative' }}>
                    <motion.div
                      whileHover={{ background: '#404040' }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '12px',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        gap: '12px'
                      }}
                      onClick={() => {
                        if (item.hasSubmenu) {
                          setShowConnectorsSubmenu(!showConnectorsSubmenu);
                        } else if (item.action) {
                          item.action();
                          setShowPlusDropdown(false);
                        }
                      }}
                    >
                      <div style={{ color: '#B8B8B8', fontSize: '16px' }}>
                        {item.icon}
                      </div>
                      <span style={{ color: '#FFFFFF', fontSize: '14px', flex: 1 }}>
                        {item.title}
                      </span>
                      {item.hasSubmenu && (
                        <RightOutlined style={{ color: '#8A8A8A', fontSize: '12px' }} />
                      )}
                    </motion.div>
                    
                    {/* Connectors Submenu */}
                    {item.hasSubmenu && showConnectorsSubmenu && (
                      <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.15 }}
                        style={{
                          position: 'absolute',
                          left: '100%',
                          top: 0,
                          background: '#2c2f2f',
                          border: '1px solid #404040',
                          borderRadius: '12px',
                          padding: '8px',
                          minWidth: '200px',
                          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
                          marginLeft: '8px'
                        }}
                      >
                        {item.submenu?.map((subItem, subIndex) => (
                          <motion.div
                            key={subItem.title}
                            whileHover={{ background: '#404040' }}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '12px',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              gap: '12px'
                            }}
                            onClick={() => {
                              if (subItem.action) {
                                subItem.action();
                              }
                              setShowPlusDropdown(false);
                              setShowConnectorsSubmenu(false);
                            }}
                          >
                            <div style={{ color: '#B8B8B8', fontSize: '16px' }}>
                              {subItem.icon}
                            </div>
                            <span style={{ color: '#FFFFFF', fontSize: '14px' }}>
                              {subItem.title}
                            </span>
                          </motion.div>
                        ))}
                      </motion.div>
                    )}
                  </div>
                ))}
              </motion.div>
            )}
          </div>
          
          <div style={{ flex: 1 }}>
            <TextArea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Ask anything"
              style={{
                background: 'transparent',
                border: 'none',
                resize: 'none',
                color: '#FFFFFF',
                fontSize: '16px',
                padding: 0,
                lineHeight: '24px',
                boxShadow: 'none',
                outline: 'none'
              }}
              autoSize={{ minRows: 1, maxRows: 6 }}
              ref={composerRef}
              onPressEnter={(e) => {
                if (!e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              onFocus={(e) => {
                e.target.style.boxShadow = 'none';
                e.target.style.outline = 'none';
              }}
            />
          </div>
          
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {(isLoading || streamingId) ? (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  if (isLoading) {
                    if (pendingTimerRef.current) {
                      clearTimeout(pendingTimerRef.current);
                      pendingTimerRef.current = null;
                    }
                    setIsLoading(false);
                  } else if (streamingId) {
                    setStopRequestedFor(streamingId);
                    setCompletedResponses(prev => new Set(Array.from(prev).concat(streamingId)));
                    setStreamingId(null);
                  }
                }}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: '#3A3A38',
                  border: 'none',
                  color: '#FFFFFF',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: '600',
                  boxShadow: 'none'
                }}
              >
                <div style={{
                  width: '10px',
                  height: '10px',
                  background: '#FFFFFF',
                  borderRadius: '2px'
                }} />
              </motion.button>
            ) : (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSend}
                disabled={!message.trim()}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: message.trim() ? 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)' : '#3A3A38',
                  border: 'none',
                  color: '#FFFFFF',
                  cursor: message.trim() ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  boxShadow: message.trim() ? '0 2px 8px rgba(59, 130, 246, 0.3)' : 'none',
                  transition: 'all 0.2s ease'
                }}
              >
                <SendIcon style={{ width: '18px', height: '18px', fill: '#FFFFFF' }} />
              </motion.button>
            )}
          </div>
        </div>
      </div>
      ) : (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '20px',
        maxWidth: '900px',
        margin: '0 auto'
      }}>
        {/* Greeting Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          style={{
            textAlign: 'center',
            marginBottom: '32px'
          }}
        >
          {/* X Logo */}
          <div style={{ marginBottom: '20px', position: 'relative', display: 'inline-block' }}>
            <img
              src="https://procurexuatstg.blob.core.windows.net/procurexuatblob/prc7.png"
              alt="X Logo"
              style={{
                height: '48px',
                width: 'auto'
              }}
            />
            {/* BETA Badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              style={{
                position: 'absolute',
                top: '-10px',
                right: '-35px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
                color: '#FFFFFF',
                fontSize: '10px',
                fontWeight: '700',
                padding: '3px 8px',
                borderRadius: '5px',
                letterSpacing: '0.5px',
                boxShadow: '0 2px 12px rgba(59, 130, 246, 0.5)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
            >
              BETA
            </motion.div>
          </div>
          
          {/* Rolling Greeting Text */}
          <div style={{
            color: '#FFFFFF',
            fontWeight: '400',
            margin: 0,
            letterSpacing: '-0.02em',
            height: '80px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <AnimatePresence mode="wait">
              <motion.h1
                key={currentTextIndex}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.8 }}
                style={{
                  color: '#FFFFFF',
                  fontSize: '36px',
                  fontWeight: '400',
                  margin: 0,
                  letterSpacing: '-0.02em'
                }}
              >
                {rollingTexts[currentTextIndex]}
              </motion.h1>
            </AnimatePresence>
          </div>
        </motion.div>

        {/* Input Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          style={{
            width: '100%',
            maxWidth: '700px',
            marginBottom: '32px'
          }}
        >
          <div style={{
            background: '#1f2121',
            border: '1px solid #2c2f2f',
            borderRadius: '32px',
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            minHeight: '56px',
            position: 'relative'
          }}>
            <div ref={dropdownRef} style={{ position: 'relative' }}>
              <Button
                type="text"
                icon={<PlusOutlined />}
                onClick={() => setShowPlusDropdown(!showPlusDropdown)}
                style={{
                  color: '#8A8A8A',
                  border: 'none',
                  background: 'transparent',
                  width: '24px',
                  height: '24px',
                  minWidth: '24px',
                  padding: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              />
              
              {/* Plus Dropdown Menu */}
              {showPlusDropdown && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  transition={{ duration: 0.15 }}
                  style={{
                    position: 'absolute',
                    bottom: '40px',
                    left: '-12px',
                    background: '#2c2f2f',
                    border: '1px solid #404040',
                    borderRadius: '12px',
                    padding: '8px',
                    minWidth: '240px',
                    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
                    zIndex: 1000
                  }}
                >
                  {dropdownMenuItems.map((item, index) => (
                    <div key={item.title} style={{ position: 'relative' }}>
                      <motion.div
                        whileHover={{ background: '#404040' }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '12px',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          gap: '12px'
                        }}
                        onClick={() => {
                          if (item.hasSubmenu) {
                            setShowConnectorsSubmenu(!showConnectorsSubmenu);
                          } else if (item.action) {
                            item.action();
                            setShowPlusDropdown(false);
                          }
                        }}
                      >
                        <div style={{ color: '#B8B8B8', fontSize: '16px' }}>
                          {item.icon}
                        </div>
                        <span style={{ color: '#FFFFFF', fontSize: '14px', flex: 1 }}>
                          {item.title}
                        </span>
                        {item.hasSubmenu && (
                          <RightOutlined style={{ color: '#8A8A8A', fontSize: '12px' }} />
                        )}
                      </motion.div>
                      
                      {/* Connectors Submenu */}
                      {item.hasSubmenu && showConnectorsSubmenu && (
                        <motion.div
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          exit={{ opacity: 0, x: -10 }}
                          transition={{ duration: 0.15 }}
                          style={{
                            position: 'absolute',
                            left: '100%',
                            top: 0,
                            background: '#2c2f2f',
                            border: '1px solid #404040',
                            borderRadius: '12px',
                            padding: '8px',
                            minWidth: '200px',
                            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4)',
                            marginLeft: '8px'
                          }}
                        >
                          {item.submenu?.map((subItem, subIndex) => (
                            <motion.div
                              key={subItem.title}
                              whileHover={{ background: '#404040' }}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '12px',
                                borderRadius: '8px',
                                cursor: 'pointer',
                                gap: '12px'
                              }}
                              onClick={() => {
                                if (subItem.action) {
                                  subItem.action();
                                }
                                setShowPlusDropdown(false);
                                setShowConnectorsSubmenu(false);
                              }}
                            >
                              <div style={{ color: '#B8B8B8', fontSize: '16px' }}>
                                {subItem.icon}
                              </div>
                              <span style={{ color: '#FFFFFF', fontSize: '14px' }}>
                                {subItem.title}
                              </span>
                            </motion.div>
                          ))}
                        </motion.div>
                      )}
                    </div>
                  ))}
                </motion.div>
              )}
            </div>
            
            <div style={{ flex: 1 }}>
              <TextArea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Ask anything"
                style={{
                  background: 'transparent',
                  border: 'none',
                  resize: 'none',
                  color: '#FFFFFF',
                  fontSize: '16px',
                  padding: 0,
                  lineHeight: '24px',
                  boxShadow: 'none',
                  outline: 'none'
                }}
                autoSize={{ minRows: 1, maxRows: 6 }}
                onPressEnter={(e) => {
                  if (!e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                onFocus={(e) => {
                  e.target.style.boxShadow = 'none';
                  e.target.style.outline = 'none';
                }}
              />
            </div>
            
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={handleSend}
                disabled={!message.trim()}
                style={{
                  width: '36px',
                  height: '36px',
                  borderRadius: '50%',
                  background: message.trim() ? 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)' : '#3A3A38',
                  border: 'none',
                  color: '#FFFFFF',
                  cursor: message.trim() ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '16px',
                  boxShadow: message.trim() ? '0 2px 8px rgba(59, 130, 246, 0.3)' : 'none',
                  transition: 'all 0.2s ease'
                }}
              >
                <SendIcon style={{ width: '18px', height: '18px', fill: '#FFFFFF' }} />
              </motion.button>
            </div>
          </div>
        </motion.div>

        {/* Suggestion Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          style={{
            display: 'flex',
            gap: '12px',
            width: '100%',
            maxWidth: '700px',
            overflowX: 'auto',
            paddingBottom: '4px',
            justifyContent: 'center'
          }}
        >
          {suggestionCards.map((card, index) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.6 + index * 0.05 }}
              whileHover={{ 
                scale: 1.02,
                transition: { duration: 0.2 }
              }}
              whileTap={{ scale: 0.98 }}
              style={{
                background: '#1f2121',
                border: '1px solid #2c2f2f',
                borderRadius: '24px',
                padding: '12px 16px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                minWidth: 'fit-content',
                whiteSpace: 'nowrap'
              }}
              onClick={() => {
                setSelectedCard(card.title);
                setShowQuestionsPopup(true);
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <div style={{ 
                  color: '#B8B8B8', 
                  fontSize: '16px'
                }}>
                  {card.icon}
                </div>
                <span style={{
                  color: '#FFFFFF',
                  fontSize: '14px',
                  fontWeight: '500'
                }}>
                  {card.title}
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Questions Popup Overlay */}
        <AnimatePresence>
          {showQuestionsPopup && (
            <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000,
              padding: '20px'
            }}
            onClick={() => {
              setShowQuestionsPopup(false);
              setSelectedCard('');
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{
                background: '#1f2121',
                border: '1px solid #2c2f2f',
                borderRadius: '20px',
                padding: '32px',
                maxWidth: '500px',
                width: '100%',
                maxHeight: '80vh',
                overflowY: 'auto'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: '24px'
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <div style={{ color: '#B8B8B8', fontSize: '20px' }}>
                    {suggestionCards.find(card => card.title === selectedCard)?.icon}
                  </div>
                  <h3 style={{
                    color: '#FFFFFF',
                    fontSize: '18px',
                    fontWeight: '600',
                    margin: 0
                  }}>
                    {selectedCard}
                  </h3>
                </div>
                <Button
                  type="text"
                  onClick={() => {
                    setShowQuestionsPopup(false);
                    setSelectedCard('');
                  }}
                  style={{
                    color: '#8A8A8A',
                    fontSize: '16px',
                    padding: '4px',
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ×
                </Button>
              </div>

              {/* Questions */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <p style={{
                  color: '#B8B8B8',
                  fontSize: '14px',
                  margin: '0 0 16px 0'
                }}>
                  Choose a question to get started:
                </p>
                
                {cardQuestions[selectedCard as keyof typeof cardQuestions]?.map((question, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    whileHover={{ scale: 1.02, backgroundColor: '#2c2f2f' }}
                    whileTap={{ scale: 0.98 }}
                    style={{
                      padding: '16px',
                      background: '#242426',
                      border: '1px solid #2c2f2f',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease'
                    }}
                    onClick={() => handleQuestionSelect(question)}
                  >
                    <span style={{
                      color: '#FFFFFF',
                      fontSize: '14px',
                      lineHeight: '1.4'
                    }}>
                      {question}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </motion.div>
          )}
        </AnimatePresence>

        {/* Bottom Disclaimer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          style={{
            textAlign: 'center',
            marginTop: '40px',
            paddingBottom: '20px'
          }}
        >
          <p style={{
            color: '#8A8A8A',
            fontSize: '12px',
            margin: 0,
            lineHeight: '1.4'
          }}>
            Beta • ControlX can make mistakes. Always verify construction data and project information.
          </p>
        </motion.div>
      </div>
      )}

      {/* Floating Elements - Always Available */}
      {/* Bottom Quick Questions Panel - Compact Size */}
      <motion.div
        style={{
          position: 'fixed',
          bottom: showBottomPanel ? '0px' : '-250px',
          right: '20px',
          width: '200px',
          height: '250px',
          background: 'linear-gradient(180deg, rgba(44, 47, 47, 0.95), rgba(26, 26, 27, 0.95))',
          backdropFilter: 'blur(15px)',
          border: '1px solid #404040',
          borderRadius: '12px 12px 0px 0px',
          zIndex: 1000,
          padding: '16px',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}
        animate={{ 
          bottom: showBottomPanel ? '0px' : '-250px'
        }}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        {/* Panel Header */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '8px'
        }}>
          <h3 style={{
            color: '#FFFFFF',
            fontSize: '16px',
            fontWeight: '600',
            margin: 0
          }}>
            Quick Questions
          </h3>
          <Button
            type="text"
            onClick={() => setShowBottomPanel(false)}
            style={{
              color: '#8A8A8A',
              fontSize: '16px',
              padding: '4px',
              width: '24px',
              height: '24px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </Button>
        </div>

        {/* Menu List */}
        <div className="hide-scrollbar" style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '4px',
          flex: 1,
          overflowY: 'auto'
        }}>
          {suggestionCards.map((card, index) => (
            <motion.div
              key={card.title}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ backgroundColor: selectedCard === card.title ? 'rgba(37, 99, 235, 0.4)' : 'rgba(64, 64, 64, 0.3)' }}
              whileTap={{ scale: 0.98 }}
              style={{
                background: selectedCard === card.title ? 'rgba(37, 99, 235, 0.2)' : 'transparent',
                border: selectedCard === card.title ? '1px solid #2563EB' : '1px solid transparent',
                borderRadius: '6px',
                padding: '8px 12px',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '10px',
                textAlign: 'left',
                transition: 'all 0.2s ease',
                minHeight: '32px'
              }}
              onClick={() => {
                console.log('🎯 Module clicked:', card.title);
                console.log('📋 Questions for this module:', cardQuestions[card.title as keyof typeof cardQuestions]);
                setSelectedCard(card.title);
                setShowQuestionsPopup(true);
                setShowBottomPanel(false);
                console.log('✅ State updated - showQuestionsPopup: true');
              }}
            >
              <div style={{ 
                color: selectedCard === card.title ? '#2563EB' : '#B8B8B8', 
                fontSize: '14px',
                minWidth: '16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {card.icon}
              </div>
              <span style={{
                color: selectedCard === card.title ? '#FFFFFF' : '#FFFFFF',
                fontSize: '12px',
                fontWeight: '500',
                lineHeight: '1.2',
                flex: 1
              }}>
                {card.title}
              </span>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Compact Toggle Button for Bottom Panel - Only show when messages exist */}
      {chatMessages.length > 0 && (
        <motion.div
          style={{
            position: 'fixed',
            bottom: showBottomPanel ? '270px' : '20px',
            right: '20px',
            width: '56px',
            height: '56px',
            background: '#2563EB',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            border: '2px solid #FFFFFF',
            zIndex: 9999,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.6)'
          }}
          animate={{ 
            bottom: showBottomPanel ? '270px' : '20px'
          }}
          transition={{ type: 'spring', stiffness: 300, damping: 30 }}
          whileHover={{ scale: 1.1, backgroundColor: '#1E40AF' }}
          whileTap={{ scale: 0.9 }}
          onClick={() => setShowBottomPanel(!showBottomPanel)}
        >
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '2px'
          }}>
            <QuestionOutlined style={{ 
              color: '#FFFFFF', 
              fontSize: '20px'
            }} />
            <span style={{ 
              color: '#FFFFFF', 
              fontSize: '8px',
              fontWeight: 'bold'
            }}>
              MENU
            </span>
          </div>
        </motion.div>
      )}

      {/* Mobile Responsive Styles */}
      <style>{`
        @media (max-width: 768px) {
          /* Make suggestion cards stack vertically on mobile */
          .suggestion-cards {
            flex-direction: column !important;
            align-items: stretch !important;
          }

          /* Adjust font sizes for mobile */
          h1 {
            font-size: 28px !important;
          }

          /* Make composer full width on mobile */
          .composer-container {
            max-width: 100% !important;
          }

          /* Adjust message padding for mobile */
          .message-content {
            padding: 12px !important;
            font-size: 14px !important;
          }

          /* Hide scrollbar on mobile for cleaner look */
          ::-webkit-scrollbar {
            width: 3px;
          }
        }

        @media (max-width: 480px) {
          /* Even smaller adjustments for very small screens */
          h1 {
            font-size: 24px !important;
          }

          .composer-container {
            padding: 12px !important;
          }
        }
      `}</style>

      {/* Details Modal for Table & Visualizations */}
      <Modal
        title={
          <span style={{ color: '#FFFFFF', fontSize: '18px', fontWeight: '600' }}>
            Data & Insights
          </span>
        }
        open={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        footer={null}
        width={1200}
        centered
        styles={{
          body: { background: '#0A0A0B', minHeight: '500px', padding: '24px' },
          header: { background: '#1A1A1B', borderBottom: '1px solid #2c2f2f' },
          content: { background: '#1A1A1B' }
        }}
      >
        {selectedMessageData && (() => {
          try {
            // Convert table from {headers: [], rows: [[]]} to array of objects
            let tableData: any[] = [];
            if (selectedMessageData.table?.headers && selectedMessageData.table?.rows) {
              const headers = selectedMessageData.table.headers;
              tableData = selectedMessageData.table.rows.map((row: any[]) => {
                const obj: any = {};
                headers.forEach((header: string, index: number) => {
                  obj[header] = row[index];
                });
                return obj;
              });
            }

            const tabItems = [];

            // Add Visualization Tab if available
            if (selectedMessageData.visualization) {
              tabItems.push({
                key: 'visualization',
                label: (
                  <span style={{ color: '#B8B8B8', fontSize: '14px' }}>
                    📈 Visualization
                  </span>
                ),
                children: (() => {
                  try {
                    return (
                      <ReactECharts
                        option={enhanceChartOptions(selectedMessageData.visualization)}
                        style={{ height: '600px', width: '100%' }}
                        opts={{ renderer: 'svg' }}
                      />
                    );
                  } catch (error) {
                    console.warn('Visualization rendering error in modal (ignoring):', error);
                    return (
                      <Empty
                        description={<span style={{ color: '#8A8A8A' }}>Unable to render visualization</span>}
                        style={{ marginTop: '60px' }}
                      />
                    );
                  }
                })()
              });
            }

            // Add Table Tab if data available
            if (tableData.length > 0) {
              tabItems.push({
                key: 'table',
                label: (
                  <span style={{ color: '#B8B8B8', fontSize: '14px' }}>
                    📋 Table Data
                  </span>
                ),
                children: (
                  <Table
                    dataSource={tableData}
                    columns={Object.keys(tableData[0] || {}).map(key => ({
                      title: key,
                      dataIndex: key,
                      key: key,
                      sorter: (a: any, b: any) => {
                        if (typeof a[key] === 'number' && typeof b[key] === 'number') {
                          return a[key] - b[key];
                        }
                        return String(a[key]).localeCompare(String(b[key]));
                      }
                    }))}
                    pagination={{ pageSize: 10, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'] }}
                    scroll={{ x: true }}
                    size="small"
                  />
                )
              });
            }

            return (
              <Tabs
                defaultActiveKey={selectedMessageData.visualization ? "visualization" : "table"}
                items={tabItems}
                tabBarStyle={{
                  borderBottom: '1px solid #2c2f2f',
                  marginBottom: '24px'
                }}
              />
            );
          } catch (error) {
            console.warn('Table/modal rendering error (ignoring):', error);
            return (
              <Empty
                description={<span style={{ color: '#8A8A8A' }}>Unable to load data</span>}
                style={{ marginTop: '60px' }}
              />
            );
          }
        })()}
      </Modal>
    </>
  );
};

export default NewChat;
